<app-social-shell active="communities">
  <a class="so-back" routerLink="/communities"><i class="fa fa-arrow-left"></i> Communities</a>

  @if (loading) {
    <div class="so-card so-hero" aria-busy="true" aria-label="Loading community">
      <div class="so-hero-cover"></div>
      <div class="so-hero-body">
        <span class="so-avatar so-avatar--xl so-avatar--square so-skel"></span>
        <div class="so-hero-info"><span class="so-skel" style="height: 22px; width: 55%"></span></div>
      </div>
    </div>
  } @else if (notFound) {
    <div class="so-card so-empty" role="alert">
      <span class="so-empty-icon"><i class="fa fa-compass"></i></span>
      <h3>Community not found</h3>
      <p>It may have been deleted, or the link is wrong.</p>
      <a class="btn btn-dark" routerLink="/communities">Browse communities</a>
    </div>
  } @else if (loadFailed) {
    <div class="so-card so-empty" role="alert">
      <span class="so-empty-icon"><i class="fa fa-wifi"></i></span>
      <h3>Couldn't load this community</h3>
      <p>Check your connection and try again.</p>
      <button type="button" class="btn btn-theme" (click)="retryLoad()">Try again</button>
    </div>
  } @else if (community) {
    <!-- ===== Header ===== -->
    <section class="so-card so-hero">
      <div class="so-hero-cover"></div>
      <div class="so-hero-body">
        <span class="so-avatar so-avatar--xl so-avatar--square">
          @if (community.image) {
            <img [src]="imageUrl(community.image)" alt="" (error)="community.image = null" />
          } @else {
            {{ initials(community.name) }}
          }
        </span>
        <div class="so-hero-info">
          <h1>{{ community.name }}</h1>
          <div class="so-hero-meta">
            <span class="so-badge" [class.so-badge--good]="community.privacy === 'public'">
              <i class="fa" [class.fa-globe]="community.privacy === 'public'" [class.fa-lock]="community.privacy === 'private'"></i>
              {{ community.privacy === 'public' ? 'Public' : 'Private' }}
            </span>
            <span>{{ community.member_count }} member{{ community.member_count === 1 ? '' : 's' }}</span>
            @if (community.my_role) {
              <span class="so-badge so-badge--accent">{{ community.my_role === 'member' ? 'Member' : roleLabel(community.my_role) }}</span>
            }
          </div>
        </div>
        <div class="so-hero-actions">
          @if (community.can_manage) {
            <a class="btn btn-dark" [routerLink]="['/communities', community.slug, 'settings']"><i class="fa fa-cog"></i> Settings</a>
          }
          @if (community.my_role) {
            @if (community.my_role !== 'owner') {
              <button type="button" class="btn btn-dark" [disabled]="busy" (click)="pendingLeave = true">Leave</button>
            }
          } @else if (community.my_invite_id) {
            <button type="button" class="btn btn-dark" [disabled]="busy" (click)="declineInvite()">Decline</button>
            <button type="button" class="btn btn-theme" [disabled]="busy" (click)="acceptInvite()">Accept invite</button>
          } @else if (community.privacy === 'public') {
            <button type="button" class="btn btn-theme" [disabled]="busy" (click)="join()"><i class="fa fa-plus"></i> {{ busy ? 'Joining…' : 'Join' }}</button>
          } @else if (community.my_request_pending) {
            <span class="so-badge so-badge--warn"><i class="fa fa-clock-o"></i> Request sent</span>
            <button type="button" class="btn btn-dark" [disabled]="busy" (click)="cancelRequest()">Cancel request</button>
          } @else {
            <button type="button" class="btn btn-theme" [disabled]="busy" (click)="showRequestForm = !showRequestForm">Ask to join</button>
          }
        </div>
      </div>
      @if (community.description) {
        <p class="so-hero-desc">{{ community.description }}</p>
      }
      @if (community.my_invite_id && !community.my_role) {
        <p class="cm-banner"><i class="fa fa-envelope-open"></i> You've been invited to join this community.</p>
      }
      @if (showRequestForm && !community.my_role && !community.my_request_pending) {
        <div class="cm-request">
          <label for="cm-request-msg">Add a note for the moderators (optional)</label>
          <textarea id="cm-request-msg" rows="2" maxlength="300" [(ngModel)]="joinMessage"
            placeholder="Who you are, or why you'd like to join"></textarea>
          <div class="cm-request-actions">
            <span class="so-faint">{{ joinMessage.length }}/300</span>
            <button type="button" class="btn btn-dark" (click)="showRequestForm = false">Cancel</button>
            <button type="button" class="btn btn-theme" [disabled]="busy" (click)="sendRequest()">{{ busy ? 'Sending…' : 'Send request' }}</button>
          </div>
        </div>
      }
    </section>

    <!-- ===== Tabs ===== -->
    <nav class="so-tabs" role="tablist" aria-label="Community sections">
      @if (community.can_see_posts) {
        <button role="tab" [attr.aria-selected]="activeTab === 'posts'" (click)="selectTab('posts')">Posts</button>
      }
      <button role="tab" [attr.aria-selected]="activeTab === 'about'" (click)="selectTab('about')">About</button>
      @if (community.can_see_posts) {
        <button role="tab" [attr.aria-selected]="activeTab === 'members'" (click)="selectTab('members')">Members</button>
      }
      @if (community.can_manage) {
        <button role="tab" [attr.aria-selected]="activeTab === 'requests'" (click)="selectTab('requests')">
          Requests
          @if (community.pending_requests) {
            <span class="so-count">{{ community.pending_requests }}</span>
          }
        </button>
      }
      @if (community.can_invite) {
        <button role="tab" [attr.aria-selected]="activeTab === 'invite'" (click)="selectTab('invite')">Invite</button>
      }
      @if (community.can_manage) {
        <button role="tab" [attr.aria-selected]="activeTab === 'moderation'" (click)="selectTab('moderation')">Moderation</button>
      }
    </nav>

    <!-- ===== Posts ===== -->
    @if (activeTab === 'posts') {
      <div class="so-stack">
        @if (community.my_role) {
          <app-social-composer [target]="{ community: community.id }" placeholder="Write a post…" (posted)="loadPosts()"></app-social-composer>
        } @else {
          <div class="so-card cm-join-hint">
            <span>Join this community to post and comment.</span>
            @if (community.privacy === 'public') {
              <button type="button" class="btn btn-theme" [disabled]="busy" (click)="join()">Join</button>
            }
          </div>
        }
        @if (postsLoading) {
          <div class="so-card so-card-pad"><span class="so-skel" style="height: 14px"></span><span class="so-skel" style="height: 14px; width: 60%; margin-top: 10px"></span></div>
        } @else if (postsError) {
          <div class="so-card so-empty">
            <h3>Couldn't load posts</h3>
            <button type="button" class="btn btn-theme" (click)="loadPosts()">Try again</button>
          </div>
        } @else {
          @for (post of posts; track post.id) {
            <app-social-post-card [post]="post" [showSource]="false" (removed)="onRemoved($event)" (pinChanged)="loadPosts()"></app-social-post-card>
          } @empty {
            <div class="so-card so-empty">
              <span class="so-empty-icon"><i class="fa fa-comments-o"></i></span>
              <h3>No posts yet</h3>
              <p>{{ community.my_role ? 'Start the conversation above.' : 'Posts from members will show up here.' }}</p>
            </div>
          }
          @if (hasMorePosts) {
            <button type="button" class="so-more" [disabled]="loadingMore" (click)="loadMorePosts()">{{ loadingMore ? 'Loading…' : 'Load more' }}</button>
          }
        }
      </div>
    }

    <!-- ===== About ===== -->
    @if (activeTab === 'about') {
      <div class="so-card so-card-pad cm-about">
        <h2 class="so-section-title">About</h2>
        <p>{{ community.description || 'No description yet.' }}</p>
        @if (community.rules) {
          <h2 class="so-section-title">Rules</h2>
          <p class="cm-rules">{{ community.rules }}</p>
        }
        <dl>
          <dt>Who can join</dt>
          <dd>{{ community.privacy === 'public' ? 'Anyone. Posts are visible to everyone.' : 'By request or invite. Posts are visible to members only.' }}</dd>
          <dt>Members</dt>
          <dd>{{ community.member_count }}</dd>
          <dt>Owner</dt>
          <dd><a (click)="openProfile(community.owner.id)">{{ community.owner.name }}</a></dd>
          <dt>Created</dt>
          <dd>{{ community.created_at | date: 'mediumDate' }}</dd>
        </dl>
        @if (!community.can_see_posts) {
          <p class="cm-banner"><i class="fa fa-lock"></i> This community is private. Its posts and members are only visible to members.</p>
        }
      </div>
    }

    <!-- ===== Members ===== -->
    @if (activeTab === 'members') {
      <div class="so-search cm-search">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input type="search" placeholder="Search members" aria-label="Search members"
          [(ngModel)]="memberQuery" (ngModelChange)="onMemberQuery($event)" />
      </div>
      <div class="so-card">
        @if (membersLoading) {
          <div class="so-row"><span class="so-avatar so-skel"></span><span class="so-skel" style="flex: 1; height: 14px"></span></div>
        } @else {
          <ul class="so-list">
            @for (member of members; track member.user.id) {
              <li class="so-row">
                <button type="button" class="so-person" (click)="openProfile(member.user.id)">
                  <span class="so-avatar">
                    @if (member.user.avatar) { <img [src]="imageUrl(member.user.avatar)" alt="" (error)="member.user.avatar = null" /> } @else { {{ initials(member.user.name) }} }
                  </span>
                  <span class="so-person-text">
                    <strong>{{ member.user.name }}</strong>
                    <span>{{ member.user.handle ? '@' + member.user.handle + ' · ' : '' }}joined {{ member.joined_at | date: 'MMM y' }}</span>
                  </span>
                </button>
                @if (member.role !== 'member') {
                  <span class="so-badge" [class.so-badge--accent]="member.role === 'owner'">{{ roleLabel(member.role) }}</span>
                }
                @if (canActOn(member)) {
                  <div class="cm-menu">
                    <button type="button" class="so-icon-btn cm-menu-btn" aria-label="Member actions" [attr.aria-expanded]="memberMenuFor === member.user.id"
                      (click)="toggleMemberMenu(member)"><i class="fa fa-ellipsis-h"></i></button>
                    @if (memberMenuFor === member.user.id) {
                      <div class="cm-menu-list" role="menu">
                        @if (community.my_role === 'owner') {
                          @if (member.role === 'member') {
                            <button type="button" role="menuitem" (click)="setRole(member, 'moderator')"><i class="fa fa-shield"></i> Make moderator</button>
                          } @else {
                            <button type="button" role="menuitem" (click)="setRole(member, 'member')"><i class="fa fa-shield"></i> Remove moderator role</button>
                          }
                          <button type="button" role="menuitem" (click)="askMemberAction(member, 'transfer')"><i class="fa fa-key"></i> Make owner</button>
                        }
                        <button type="button" role="menuitem" (click)="askMemberAction(member, 'remove')"><i class="fa fa-user-times"></i> Remove from community</button>
                        <button type="button" role="menuitem" class="is-danger" (click)="askMemberAction(member, 'ban')"><i class="fa fa-ban"></i> Ban</button>
                      </div>
                    }
                  </div>
                }
              </li>
            } @empty {
              <li class="so-empty"><h3>{{ memberQuery ? 'No members match that name' : 'No members to show' }}</h3></li>
            }
          </ul>
        }
      </div>
      @if (hasMoreMembers) {
        <button type="button" class="so-more cm-gap" (click)="loadMembers(true)">Load more</button>
      }
    }

    <!-- ===== Requests ===== -->
    @if (activeTab === 'requests') {
      <div class="so-card">
        @if (requestsLoading) {
          <div class="so-row"><span class="so-avatar so-skel"></span><span class="so-skel" style="flex: 1; height: 14px"></span></div>
        } @else {
          <ul class="so-list">
            @for (req of requests; track req.id) {
              <li class="so-row cm-request-row">
                <button type="button" class="so-person" (click)="openProfile(req.user.id)">
                  <span class="so-avatar">
                    @if (req.user.avatar) { <img [src]="imageUrl(req.user.avatar)" alt="" (error)="req.user.avatar = null" /> } @else { {{ initials(req.user.name) }} }
                  </span>
                  <span class="so-person-text">
                    <strong>{{ req.user.name }}</strong>
                    <span>Asked {{ timeAgo(req.created_at) }} ago</span>
                  </span>
                </button>
                <div class="so-row-actions">
                  <button type="button" class="btn btn-dark" [disabled]="deciding.has(req.id)" (click)="decide(req, false)">Decline</button>
                  <button type="button" class="btn btn-theme" [disabled]="deciding.has(req.id)" (click)="decide(req, true)">Approve</button>
                </div>
                @if (req.message) {
                  <p class="cm-request-msg">“{{ req.message }}”</p>
                }
              </li>
            } @empty {
              <li class="so-empty">
                <span class="so-empty-icon"><i class="fa fa-inbox"></i></span>
                <h3>No one is waiting to join</h3>
              </li>
            }
          </ul>
        }
      </div>
      @if (hasMoreRequests) {
        <button type="button" class="so-more cm-gap" (click)="loadRequests(true)">Load more</button>
      }
    }

    <!-- ===== Invite ===== -->
    @if (activeTab === 'invite') {
      <div class="so-stack">
        <section class="so-card so-card-pad">
          <h2 class="so-section-title">Invite people</h2>
          <p class="so-muted cm-hint">They'll get a notification and can accept or decline. Invites last 14 days.</p>
          <div class="so-search">
            <i class="fa fa-search" aria-hidden="true"></i>
            <input type="search" placeholder="Search by name or @handle" aria-label="Search people to invite"
              [(ngModel)]="inviteQuery" (ngModelChange)="onInviteQuery($event)" />
          </div>
          @if (inviteSearching) {
            <p class="so-muted cm-hint-top">Searching…</p>
          } @else if (inviteQuery.trim().length >= 2) {
            <ul class="so-list cm-results">
              @for (person of inviteResults; track person.id) {
                <li class="so-row">
                  <span class="so-person so-person--static">
                    <span class="so-avatar so-avatar--sm">
                      @if (person.avatar) { <img [src]="imageUrl(person.avatar)" alt="" (error)="person.avatar = null" /> } @else { {{ initials(person.name) }} }
                    </span>
                    <span class="so-person-text">
                      <strong>{{ person.name }}</strong>
                      @if (person.handle) { <span>&#64;{{ person.handle }}</span> }
                    </span>
                  </span>
                  <button type="button" class="btn" [class.btn-theme]="!invitedIds.has(person.id)" [class.btn-dark]="invitedIds.has(person.id)"
                    [disabled]="invitedIds.has(person.id)" (click)="sendInvite(person)">
                    {{ invitedIds.has(person.id) ? 'Invited' : 'Invite' }}
                  </button>
                </li>
              } @empty {
                <li class="so-muted cm-hint-top">No one found with that name.</li>
              }
            </ul>
          }
        </section>

        @if (community.can_manage) {
          <section class="so-card">
            <div class="so-card-pad cm-section-head"><h2 class="so-section-title">Open invites</h2></div>
            @if (invitesLoading) {
              <div class="so-row"><span class="so-skel" style="flex: 1; height: 14px"></span></div>
            } @else {
              <ul class="so-list">
                @for (inv of invites; track inv.id) {
                  <li class="so-row">
                    <span class="so-person so-person--static">
                      <span class="so-avatar so-avatar--sm">{{ initials(inv.invitee.name) }}</span>
                      <span class="so-person-text">
                        <strong>{{ inv.invitee.name }}</strong>
                        <span>Invited by {{ inv.inviter?.name || 'someone' }} · expires {{ inv.expires_at | date: 'MMM d' }}</span>
                      </span>
                    </span>
                    <button type="button" class="btn btn-dark" (click)="revokeInvite(inv)">Cancel</button>
                  </li>
                } @empty {
                  <li class="so-muted cm-empty-row">No open invites.</li>
                }
              </ul>
            }
          </section>

          <section class="so-card so-card-pad">
            <h2 class="so-section-title">Invite links</h2>
            <p class="so-muted cm-hint">Anyone with a link can join directly, even while the community is private. Turn a link off any time.</p>
            <div class="cm-link-form">
              <label>
                <span>Expires after</span>
                <select [(ngModel)]="linkDays">
                  <option [ngValue]="1">1 day</option>
                  <option [ngValue]="7">7 days</option>
                  <option [ngValue]="30">30 days</option>
                  <option [ngValue]="null">Never</option>
                </select>
              </label>
              <label>
                <span>Max uses</span>
                <select [(ngModel)]="linkMaxUses">
                  <option [ngValue]="null">No limit</option>
                  <option [ngValue]="1">1</option>
                  <option [ngValue]="5">5</option>
                  <option [ngValue]="25">25</option>
                  <option [ngValue]="100">100</option>
                </select>
              </label>
              <button type="button" class="btn btn-theme" [disabled]="creatingLink" (click)="createLink()">
                <i class="fa fa-link"></i> {{ creatingLink ? 'Creating…' : 'Create link' }}
              </button>
            </div>
            @if (linksLoading) {
              <p class="so-muted">Loading…</p>
            } @else {
              <ul class="so-list cm-links">
                @for (link of links; track link.id) {
                  <li [class.is-off]="!link.usable">
                    <code>{{ linkUrl(link) }}</code>
                    <span class="so-faint">
                      {{ link.use_count }}{{ link.max_uses ? '/' + link.max_uses : '' }} used ·
                      @if (!link.usable) { Off } @else if (link.expires_at) { expires {{ link.expires_at | date: 'MMM d, h:mm a' }} } @else { never expires }
                    </span>
                    @if (link.usable) {
                      <span class="so-row-actions">
                        <button type="button" class="btn btn-dark" (click)="copyLink(link)"><i class="fa" [class.fa-check]="copiedLinkId === link.id" [class.fa-copy]="copiedLinkId !== link.id"></i> {{ copiedLinkId === link.id ? 'Copied' : 'Copy' }}</button>
                        <button type="button" class="btn btn-dark" (click)="revokeLink(link)">Turn off</button>
                      </span>
                    }
                  </li>
                } @empty {
                  <li class="so-muted">No links yet.</li>
                }
              </ul>
            }
          </section>
        }
      </div>
    }

    <!-- ===== Moderation ===== -->
    @if (activeTab === 'moderation') {
      <div class="so-stack">
        <section class="so-card">
          <div class="so-card-pad cm-section-head"><h2 class="so-section-title">Banned</h2></div>
          @if (bansLoading) {
            <div class="so-row"><span class="so-skel" style="flex: 1; height: 14px"></span></div>
          } @else {
            <ul class="so-list">
              @for (ban of bans; track ban.user.id) {
                <li class="so-row">
                  <span class="so-person so-person--static">
                    <span class="so-avatar so-avatar--sm">{{ initials(ban.user.name) }}</span>
                    <span class="so-person-text">
                      <strong>{{ ban.user.name }}</strong>
                      <span>By {{ ban.banned_by?.name || 'a moderator' }}, {{ ban.created_at | date: 'mediumDate' }}{{ ban.reason ? ' — ' + ban.reason : '' }}</span>
                    </span>
                  </span>
                  <button type="button" class="btn btn-dark" (click)="unban(ban)">Unban</button>
                </li>
              } @empty {
                <li class="so-muted cm-empty-row">No one is banned.</li>
              }
            </ul>
          }
        </section>

        <section class="so-card">
          <div class="so-card-pad cm-section-head"><h2 class="so-section-title">Moderation log</h2></div>
          @if (logLoading) {
            <div class="so-row"><span class="so-skel" style="flex: 1; height: 14px"></span></div>
          } @else {
            <ul class="so-list cm-log">
              @for (entry of log; track entry.id) {
                <li class="so-row">
                  <span>{{ logText(entry) }}</span>
                  <time class="so-faint">{{ entry.created_at | date: 'MMM d, h:mm a' }}</time>
                </li>
              } @empty {
                <li class="so-muted cm-empty-row">Nothing yet.</li>
              }
            </ul>
          }
        </section>
        @if (hasMoreLog) {
          <button type="button" class="so-more" (click)="loadLog(true)">Load more</button>
        }
      </div>
    }
  }
</app-social-shell>

<app-social-confirm-dialog
  [open]="pendingLeave"
  title="Leave this community?"
  [message]="community?.privacy === 'private' ? 'You will need an invite or approval to join again.' : 'You can join again any time.'"
  confirmLabel="Leave"
  [busy]="busy"
  busyLabel="Leaving…"
  (confirm)="confirmLeave()"
  (cancel)="pendingLeave = false"></app-social-confirm-dialog>

<app-social-confirm-dialog
  [open]="pendingMemberAction !== null"
  [title]="memberActionTitle"
  [message]="memberActionMessage"
  [busy]="memberActionBusy"
  busyLabel="Working…"
  [confirmLabel]="pendingMemberAction?.kind === 'transfer' ? 'Make owner' : pendingMemberAction?.kind === 'ban' ? 'Ban' : 'Remove'"
  (confirm)="confirmMemberAction()"
  (cancel)="pendingMemberAction = null"></app-social-confirm-dialog>
